import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';

// ---------- Typer ----------
type ToastVariant = 'success' | 'error' | 'info';

interface ToastOptions {
  message: string;
  variant?: ToastVariant;
  /** Varighet i ms før toasten fjernes automatisk. Default 5000. Sett til 0 for å kreve manuell lukking. */
  duration?: number;
  /** Valgfri handlingsknapp, f.eks. "Angre". */
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastItem extends Required<Pick<ToastOptions, 'message' | 'variant' | 'duration'>> {
  id: number;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastContextValue {
  /** Generisk – bruk for full kontroll (f.eks. angre-knapp). */
  show: (opts: ToastOptions) => void;
  success: (message: string, opts?: Partial<ToastOptions>) => void;
  error: (message: string, opts?: Partial<ToastOptions>) => void;
  info: (message: string, opts?: Partial<ToastOptions>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast må brukes innenfor <ToastProvider>');
  }
  return ctx;
}

// ---------- Provider ----------
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (opts: ToastOptions) => {
      const id = ++idRef.current;
      const duration = opts.duration ?? 5000;
      const item: ToastItem = {
        id,
        message: opts.message,
        variant: opts.variant ?? 'info',
        duration,
        actionLabel: opts.actionLabel,
        onAction: opts.onAction,
      };
      setToasts((prev) => [...prev, item]);
      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }
    },
    [dismiss],
  );

  const value: ToastContextValue = {
    show,
    success: (message, opts) => show({ message, variant: 'success', ...opts }),
    error: (message, opts) => show({ message, variant: 'error', duration: 7000, ...opts }),
    info: (message, opts) => show({ message, variant: 'info', ...opts }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 md:items-end md:px-6"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.variant === 'error' ? 'alert' : 'status'}
            className={clsx(
              'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-lg border px-4 py-3 shadow-lg animate-fade-in-up',
              t.variant === 'error' && 'border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
              t.variant === 'success' && 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
              t.variant === 'info' && 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200',
            )}
          >
            <span className="flex-1 text-sm">{t.message}</span>
            {t.actionLabel && (
              <button
                onClick={() => {
                  t.onAction?.();
                  dismiss(t.id);
                }}
                className="shrink-0 text-sm font-medium underline underline-offset-2 hover:no-underline"
              >
                {t.actionLabel}
              </button>
            )}
            <button
              onClick={() => dismiss(t.id)}
              className="shrink-0 text-slate-400 hover:text-slate-600"
              aria-label="Lukk varsel"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
